import { useEffect, useState } from "react";
import { SettingsAPI, HRFSettings } from "../services/settings.service";

// HRF (Health Record Format) rules are configured in clinic settings and
// enforced in the visit flow. The settings screen owns the writes; visit
// screens read these to decide whether to block a save.

const CACHE_KEY = "kgz_hrf_settings_cache";

// Fail-open default: until the real settings are known we enforce nothing, so
// a failed/slow fetch can never trap a clinician mid-visit. The server default
// has the mandatory flags ON, so enforcement engages as soon as the GET lands.
const NO_ENFORCEMENT: HRFSettings = {
  mandatory_vitals:    false,
  mandatory_diagnosis: false,
  lab_report_uploads:  false,
};

function readCache(): HRFSettings | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as HRFSettings) : null;
  } catch {
    return null;
  }
}

/** Persist the latest HRF settings so visit screens enforce them offline too. */
export function cacheHrfSettings(data: HRFSettings): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    /* localStorage unavailable — non-fatal */
  }
}

/**
 * Read the clinic's HRF settings for enforcement.
 * Seeds synchronously from the cache (so a warm cache enforces immediately),
 * then refreshes from the API. Falls back to the cache / no-enforcement when
 * the network is unavailable.
 */
export function useHrfSettings(): HRFSettings {
  const [settings, setSettings] = useState<HRFSettings>(() => readCache() ?? NO_ENFORCEMENT);

  useEffect(() => {
    let cancelled = false;
    SettingsAPI.getHRFSettings()
      .then(data => {
        if (cancelled) return;
        setSettings(data);
        cacheHrfSettings(data);
      })
      .catch(() => { /* keep cached / default — fail open */ });
    return () => { cancelled = true; };
  }, []);

  return settings;
}
